import { useState, useCallback, useRef, useEffect } from 'react';
import { Room, RoomEvent } from 'livekit-client';
import { requestLiveKitConnectV3, reportAvatarSessionEnd } from '../api/publicProposals';

/**
 * useLiveKitAvatarV3
 * ==================
 * Manages a LiveKit room connection for V3 avatar-driven proposal presentation.
 * Uses LemonSlice avatar instead of Anam.
 * Supports raise-hand and resume-presentation controls for button-driven Q&A.
 */
const useLiveKitAvatarV3 = () => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [videoTrack, setVideoTrack] = useState(null);
  const [audioTrack, setAudioTrack] = useState(null);
  const [agentState, setAgentState] = useState('idle');
  const [agentActivity, setAgentActivity] = useState('idle');
  const [avatarName, setAvatarName] = useState(null);
  const [slidesLocked, setSlidesLocked] = useState(false);

  const roomRef = useRef(null);
  const onSlideAdvanceRef = useRef(null);
  const sessionStartRef = useRef(null);
  const proposalTokenRef = useRef(null);
  const agentDidPresentRef = useRef(false);
  const lastDisconnectRef = useRef(0);

  const setOnSlideAdvance = useCallback((cb) => {
    onSlideAdvanceRef.current = cb;
  }, []);

  const sendMessage = useCallback((msg) => {
    const room = roomRef.current;
    if (!room || !room.localParticipant) return;
    const payload = new TextEncoder().encode(JSON.stringify(msg));
    room.localParticipant.publishData(payload, { reliable: true });
  }, []);

  const handleDataReceived = useCallback((data) => {
    try {
      const msg = JSON.parse(new TextDecoder().decode(data));
      if (msg.type === 'advance_slide' && typeof msg.section === 'number') {
        if (onSlideAdvanceRef.current) {
          onSlideAdvanceRef.current(msg.section);
        }
      }
      if (msg.type === 'state_change' && msg.state) {
        setAgentState(msg.state);
      }
      if (msg.type === 'slides_locked') {
        setSlidesLocked(!!msg.locked);
      }
    } catch {
      // ignore non-JSON data
    }
  }, []);

  const handleActiveSpeakersChanged = useCallback((speakers) => {
    const agentSpeaking = speakers.some(p => {
      const identity = (p.identity || '').toLowerCase();
      const attrs = p.attributes || {};
      return identity.includes('agent') || attrs['lk.agent.state'] || p.kind === 'agent';
    });
    if (agentSpeaking) {
      setAgentActivity('speaking');
    } else {
      setAgentActivity(prev => prev === 'speaking' ? 'listening' : prev);
    }
  }, []);

  const handleParticipantAttributesChanged = useCallback((changedAttrs, participant) => {
    if (!participant) return;
    const identity = (participant.identity || '').toLowerCase();
    const attrs = participant.attributes || {};
    const isAgent = identity.includes('agent') || attrs['lk.agent.state'] || participant.kind === 'agent';
    if (!isAgent) return;

    const lkState = changedAttrs?.['lk.agent.state'] || attrs['lk.agent.state'];
    if (lkState) {
      setAgentActivity(lkState);
    }
  }, []);

  const isAvatarParticipant = useCallback((participant) => {
    if (!participant) return false;
    const identity = (participant.identity || '').toLowerCase();
    if (identity.includes('avatar') || identity.includes('lemonslice')) return true;
    const attrs = participant.attributes || {};
    if (attrs['lk.publish_on_behalf'] || participant.kind === 'agent') return true;
    return false;
  }, []);

  const handleTrackSubscribed = useCallback((track, publication, participant) => {
    if (track.kind === 'video') {
      setVideoTrack(track);
      agentDidPresentRef.current = true;
    } else if (track.kind === 'audio') {
      setAudioTrack(track);
    }
  }, []);

  const handleTrackUnsubscribed = useCallback((track) => {
    if (track.kind === 'video') {
      setVideoTrack((prev) => (prev === track ? null : prev));
    } else if (track.kind === 'audio') {
      setAudioTrack((prev) => (prev === track ? null : prev));
    }
  }, []);

  const endSession = useCallback(async () => {
    if (sessionStartRef.current && proposalTokenRef.current && agentDidPresentRef.current) {
      try {
        await reportAvatarSessionEnd(proposalTokenRef.current, sessionStartRef.current);
      } catch (err) {
        console.warn('[useLiveKitAvatarV3] Failed to report session end:', err);
      }
      sessionStartRef.current = null;
      agentDidPresentRef.current = false;
    }
  }, []);

  const connect = useCallback(async (proposalToken) => {
    if (roomRef.current || isConnecting) return;
    const timeSinceDisconnect = Date.now() - lastDisconnectRef.current;
    if (lastDisconnectRef.current > 0 && timeSinceDisconnect < 5000) {
      const waitMs = 5000 - timeSinceDisconnect;
      setIsConnecting(true);
      setAgentState('idle');
      await new Promise(resolve => setTimeout(resolve, waitMs));
    }
    setIsConnecting(true);
    setError(null);
    setAgentState('idle');
    agentDidPresentRef.current = false;

    try {
      const res = await requestLiveKitConnectV3(proposalToken);
      const inner = res.data || res;
      sessionStartRef.current = inner.session_started_at;
      proposalTokenRef.current = proposalToken;
      if (inner.avatar_name) setAvatarName(inner.avatar_name);
      const livekit_url = inner.server_url;
      const livekit_token = inner.participant_token;

      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
      });

      room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
      room.on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
      room.on(RoomEvent.DataReceived, handleDataReceived);
      room.on(RoomEvent.ActiveSpeakersChanged, handleActiveSpeakersChanged);
      room.on(RoomEvent.ParticipantAttributesChanged, handleParticipantAttributesChanged);
      room.on(RoomEvent.Disconnected, () => {
        lastDisconnectRef.current = Date.now();
        endSession();
        setIsConnected(false);
        setAgentState('ended');
        setAgentActivity('idle');
        setVideoTrack(null);
        setAudioTrack(null);
        roomRef.current = null;
      });
      room.on(RoomEvent.ParticipantDisconnected, (participant) => {
        if (isAvatarParticipant(participant)) {
          endSession();
          setAgentState('ended');
          setVideoTrack(null);
          setAudioTrack(null);
        }
      });

      await room.connect(livekit_url, livekit_token);
      roomRef.current = room;

      try {
        await room.localParticipant.setMicrophoneEnabled(true);
      } catch (micErr) {
        console.warn('[useLiveKitAvatarV3] Could not enable mic:', micErr);
      }

      setIsConnected(true);
      setAgentState('presenting');
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to connect to presentation');
      setAgentState('idle');
    } finally {
      setIsConnecting(false);
    }
  }, [handleTrackSubscribed, handleTrackUnsubscribed, handleDataReceived, handleActiveSpeakersChanged, handleParticipantAttributesChanged, endSession]);

  const disconnect = useCallback(async () => {
    await endSession();
    const room = roomRef.current;
    if (room) {
      room.disconnect();
      roomRef.current = null;
    }
    lastDisconnectRef.current = Date.now();
    setIsConnected(false);
    setIsConnecting(false);
    setVideoTrack(null);
    setAudioTrack(null);
    setAgentState('idle');
    setError(null);
  }, [endSession]);

  const restart = useCallback(async () => {
    const token = proposalTokenRef.current;
    await endSession();
    const room = roomRef.current;
    if (room) {
      room.disconnect();
      roomRef.current = null;
    }
    setIsConnected(false);
    setVideoTrack(null);
    setAudioTrack(null);
    setAgentState('idle');
    setAgentActivity('idle');
    setError(null);
    agentDidPresentRef.current = false;
    await new Promise(resolve => setTimeout(resolve, 5000));
    if (token) {
      await connect(token);
    }
  }, [connect, endSession]);

  const raiseHand = useCallback(() => {
    sendMessage({ type: 'raise_hand' });
  }, [sendMessage]);

  const resumePresentation = useCallback(() => {
    sendMessage({ type: 'resume' });
  }, [sendMessage]);

  const goToSection = useCallback((index) => {
    sendMessage({ type: 'slide_change', section: index });
  }, [sendMessage]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (sessionStartRef.current && proposalTokenRef.current && agentDidPresentRef.current) {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const url = `${API_BASE_URL}/api/v1/public/proposals/${proposalTokenRef.current}/avatar/session-end`;
        const body = JSON.stringify({ session_started_at: sessionStartRef.current });
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
        sessionStartRef.current = null;
      }
      if (roomRef.current) {
        roomRef.current.disconnect();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (roomRef.current) {
        roomRef.current.disconnect();
        roomRef.current = null;
      }
    };
  }, []);

  return {
    isConnecting,
    isConnected,
    error,
    videoTrack,
    audioTrack,
    agentState,
    agentActivity,
    avatarName,
    connect,
    disconnect,
    restart,
    slidesLocked,
    raiseHand,
    resumePresentation,
    goToSection,
    setOnSlideAdvance,
  };
};

export default useLiveKitAvatarV3;
